// Configuração de rotas do sistema

import BlankLayout from '@/layouts/blank/BlankLayout.vue';
import FullLayout from '@/layouts/full/FullLayout.vue';
import type { RouteRecordRaw } from 'vue-router';
import assetsRoutes from './assets';
import ativosRoutes from './ativos';
import cartaoCorporativoRoutes from './cartaoCorporativo';
import dashboardsRoutes from './dashboards';
import financeiroRelatoriosRoutes from './financeiroRelatorios';
import comprasRelatoriosRoutes from './comprasRelatorios';
import logisticaRoutes from './logistica';
import licitacoesRoutes from './licitacoes';
import faturasTiRoutes from './faturasTi';
import auditoriaComodatoRoutes from './auditoriaComodato';
import controleAcessoRoutes from './controleAcesso';
import m365Routes from './m365';
import policiesRoutes from './policies';
import inovacaoRoutes from './inovacao';
import pedidosCompraRoutes from './pedidosCompra';
import produtosRoutes from './produtos';
import reembolsoRoutes from './reembolso';
import { identidadeRedirect } from './identidadeRedirect';
import relatorioMunicipalRoutes from './relatorioMunicipal';
import resourcesRoutes from './resources';
import wikiRoutes, { wikiPublicRoutes } from './wiki';
import { ticketRoutes, ticketPublicRoutes } from './ticket';
import ticketReportsRoutes from './ticketReports';
import compassRoutes from './compass';

export const publicRoutes: RouteRecordRaw[] = [
    {
        path: '/login',
        component: BlankLayout,
        children: [
            {
                path: '',
                name: 'Login',
                component: () => import('@/views/Login/Login.vue')
            },
            {
                // Herda hideForAuth do pai: seguro hoje porque o backend chega aqui via
                // redirect full-page (window.location), com auth ainda vazio no guard.
                // Se um dia navegarmos para cá via SPA (já autenticado), reavaliar este meta.
                path: 'sso-callback',
                name: 'SsoCallback',
                component: () => import('@/views/Login/SsoCallback.vue')
            },
            {
                // Herda hideForAuth do pai (usuário logado é redirecionado — ok, não precisa do fluxo)
                path: 'esqueci-senha',
                name: 'EsqueciSenha',
                component: () => import('@/views/Login/EsqueciSenha.vue')
            }
        ],
        meta: {
            requiresAuth: false,
            hideForAuth: true,
            title: 'Login - Hub AtlanGlobal'
        }
    },
    {
        // Record PRÓPRIO (fora do pai /login) de propósito: o guard usa
        // to.matched.some(hideForAuth), então como child o link do e-mail seria
        // silenciosamente redirecionado para "/" se houvesse sessão ativa no
        // navegador — e a página consome um token de conta específica,
        // independente da sessão. Mesma URL, sem hideForAuth.
        path: '/login/redefinir-senha',
        component: BlankLayout,
        meta: {
            requiresAuth: false,
            title: 'Redefinir senha'
        },
        children: [
            {
                path: '',
                name: 'RedefinirSenha',
                component: () => import('@/views/Login/RedefinirSenha.vue')
            }
        ]
    },
    {
        path: '/kiosk',
        component: BlankLayout,
        meta: {
            requiresAuth: true,
            kiosk: true,
            title: 'Modo Quiosque'
        },
        children: [
            {
                path: ':salaId?',
                name: 'KioskMode',
                component: () => import('@/views/Bookings/KioskMode.vue'),
                meta: {
                    requiresAuth: true,
                    kiosk: true,
                    title: 'Modo Quiosque'
                }
            }
        ]
    },
    {
        path: '/asset-photo-upload/:token',
        component: BlankLayout,
        meta: {
            requiresAuth: false,
            title: 'Upload de Fotos - ITAM'
        },
        children: [
            {
                path: '',
                name: 'AssetPhotoUploadMobile',
                component: () => import('@/views/Assets/AssetPhotoUploadMobile.vue'),
                meta: {
                    requiresAuth: false,
                    title: 'Upload de Fotos'
                }
            }
        ]
    },
    {
        // Upload mobile de comprovante de reembolso via QR Code (§11.5). Público como
        // /asset-photo-upload: o telefone não tem sessão do Hub — a autenticação é o
        // token temporário (30 min, escopado à despesa) embutido na URL.
        path: '/reembolso/comprovante-upload/:token',
        component: BlankLayout,
        meta: {
            requiresAuth: false,
            title: 'Comprovante de Reembolso'
        },
        children: [
            {
                path: '',
                name: 'ReembolsoComprovanteUploadMobile',
                component: () => import('@/views/Reembolso/ComprovanteUploadMobile.vue'),
                meta: {
                    requiresAuth: false,
                    title: 'Enviar Comprovante'
                }
            }
        ]
    },
    {
        // QR em LOTE do lançamento "comprovantes primeiro": várias fotos por QR, token escopado à SOLICITAÇÃO (30 min).
        path: '/reembolso/comprovantes-lote/:token',
        component: BlankLayout,
        meta: { requiresAuth: false, title: 'Comprovantes de Reembolso' },
        children: [
            {
                path: '',
                name: 'ReembolsoComprovantesLoteMobile',
                component: () => import('@/views/Reembolso/ComprovanteLoteUploadMobile.vue'),
                meta: { requiresAuth: false, title: 'Enviar Comprovantes' }
            }
        ]
    },
    {
        // TV pública do Kanban de separação (CD). Sem login Microsoft —
        // o PIN embutido no link cria um cookie de sessão. Fora do menu.
        path: '/separacao-tv',
        component: BlankLayout,
        meta: {
            requiresAuth: false,
            title: 'Painel de Separação · ACD'
        },
        children: [
            {
                path: '',
                name: 'SeparacaoTvNoPin',
                component: () => import('@/views/Logistica/Separacao/SeparacaoTv.vue'),
                meta: {
                    requiresAuth: false,
                    title: 'Painel de Separação · ACD'
                }
            },
            {
                path: ':pin',
                name: 'SeparacaoTv',
                component: () => import('@/views/Logistica/Separacao/SeparacaoTv.vue'),
                meta: {
                    requiresAuth: false,
                    title: 'Painel de Separação · ACD'
                }
            }
        ]
    },
    {
        path: '/kiosk-tv',
        component: BlankLayout,
        meta: {
            requiresAuth: false,
            title: 'Mídia indoor TV'
        },
        children: [
            {
                path: '',
                name: 'KioskTvNoKey',
                component: () => import('@/views/IndoorTv/KioskTv.vue'),
                meta: {
                    requiresAuth: false,
                    title: 'Mídia indoor TV'
                }
            },
            {
                path: ':palavraChave',
                name: 'KioskTv',
                component: () => import('@/views/IndoorTv/KioskTv.vue'),
                meta: {
                    requiresAuth: false,
                    title: 'Mídia indoor TV'
                }
            }
        ]
    },
    // Página pública standalone de artigo da Wiki (sem login, fora do FullLayout)
    ...wikiPublicRoutes,
    // Service Desk — formulário externo e acompanhamento (sem login)
    ...ticketPublicRoutes,
];

export const protectedRoutes: RouteRecordRaw[] = [
    {
        path: '/',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Dashboard - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'Home',
                // Gate por shell: console → Home dinâmica nova; clássico → Dashboard Vuetify.
                component: () => import('@/views/Home/HomeGate.vue'),
                meta: { title: 'Dashboard' }
            }
        ]
    },
    // Assistente (Chat Corporativo · AI Workstation). Duas rotas para o MESMO
    // componente: `/assistente` retoma a conversa mais recente (ou cria a
    // primeira) e `/assistente/:id` é a conversa em si — é este path que cada
    // sub-item do menu lateral aponta.
    {
        path: '/assistente',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Assistente - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'Assistente',
                component: () => import('@/views/Assistente/AssistenteChat.vue'),
                meta: { title: 'Assistente', breadcrumb: 'Assistente' }
            },
            {
                // ANTES de `:id`: é rota de permissão PRÓPRIA (nível admin,
                // migration 182), e não uma conversa. Segmento estático vence o
                // dinâmico no ranking do Vue Router, mas declarar aqui deixa a
                // intenção explícita para quem lê.
                path: 'admin',
                name: 'AssistenteAdmin',
                component: () => import('@/views/Assistente/AssistenteAdmin.vue'),
                meta: { title: 'Assistente · Painel IA', breadcrumb: 'Painel IA' }
            },
            {
                path: ':id',
                name: 'AssistenteConversa',
                component: () => import('@/views/Assistente/AssistenteChat.vue'),
                meta: { title: 'Assistente', breadcrumb: 'Conversa' }
            }
        ]
    },
    {
        path: '/cotacao-fretes',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Cotação de Fretes - Hub AtlanGlobal'
        },
        children: [
            {
                path: '', // Fica vazio para assumir o '/cotacao-fretes'
                name: 'CotacaoFretes',
                component: () => import('@/views/Products/FreightQuote.vue'),
                meta: {
                    title: 'Cotação de Fretes',
                    breadcrumb: 'Cotação'
                }
            },
            // Gateway de Frete · rastreio (Corporate Console). Rota REAL de
            // propósito: é o syncFrontendRoutes (login do admin master) que
            // cadastra o path e faz a permissão resolver.
            //
            // A tela antiga de cotação (Frenet, Vuetify) fica no lugar dela por
            // enquanto: o gateway ainda não cota — depende de a Rodonaves
            // confirmar o que a simulação persiste no TMS deles. Apagar a Frenet
            // antes disso deixaria o módulo sem cotação nenhuma.
            {
                path: 'rastreio',
                name: 'FreteRastreio',
                component: () => import('@/views/CotacaoFretes/FreteRastreio.vue'),
                meta: {
                    title: 'Rastreio de Frete',
                    breadcrumb: 'Rastreio'
                }
            }
        ]
    },
    {
        path: '/itsm',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'IT Service Manager'
        },
        children: [
            {
                // Absorvido pelo console M365 na Fase E. O redirect fica: links
                // antigos, favoritos e o histórico do navegador continuam
                // funcionando, e o inventário agora vem do diretório (sem as
                // env INTUNE_*, sem o filtro de nome `VIA-MDM-` e com auditoria).
                path: 'intune',
                name: 'intune',
                redirect: '/m365/dispositivos'
            }
        ]
    },
    {
        path: '/marketing',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Marketing CMS - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'marketing',
                component: () => import('@/views/Marketing/MarketingCms.vue'),
                meta: {
                    title: 'CMS',
                    breadcrumb: 'Marketing'
                }
            },
            {
                path: 'colecao',
                name: 'marketing-collection',
                component: () => import('@/views/Marketing/MarketingCollection.vue'),
                meta: {
                    title: 'Coleção',
                    breadcrumb: 'Coleção'
                }
            },
            {
                path: 'brandings',
                name: 'marketing-brandings',
                component: () => import('@/views/Marketing/MarketingBrandings.vue'),
                meta: {
                    title: 'Contextos de Marketing',
                    breadcrumb: 'Contextos'
                }
            },
            {
                path: 'planning',
                name: 'marketing-planning',
                component: () => import('@/views/Marketing/Planning/PlanningWorkspace.vue'),
                meta: {
                    title: 'Planejamento Inteligente',
                    breadcrumb: 'Planejamento'
                }
            },
            {
                path: 'strategist',
                name: 'marketing-strategist',
                component: () => import('@/views/Marketing/Planning/ChatStrategist.vue'),
                meta: {
                    title: 'Estrategista IA',
                    breadcrumb: 'Estrategista'
                }
            },
            {
                path: 'media',
                name: 'marketing-media',
                component: () => import('@/views/Marketing/MarketingMedia.vue'),
                meta: {
                    title: 'Biblioteca de Mídia',
                    breadcrumb: 'Mídia'
                }
            },
            {
                path: 'skills',
                name: 'marketing-skills',
                component: () => import('@/views/Marketing/MarketingSkills.vue'),
                meta: {
                    title: 'Biblioteca de Skills',
                    breadcrumb: 'Skills'
                }
            },
            {
                path: 'audiences',
                name: 'marketing-audiences',
                component: () => import('@/views/Marketing/MarketingAudiences.vue'),
                meta: {
                    title: 'Públicos Personalizados',
                    breadcrumb: 'Públicos'
                }
            },
            {
                path: 'ads-panel',
                name: 'marketing-ads-panel',
                component: () => import('@/views/Marketing/MarketingAdsPanel.vue'),
                meta: {
                    title: 'Painel de Anúncios',
                    breadcrumb: 'Painel de Anúncios'
                }
            },
            {
                path: 'ads-dashboard',
                name: 'marketing-ads-dashboard',
                component: () => import('@/views/Marketing/MarketingAdsDashboard.vue'),
                meta: {
                    title: 'Dashboard Meta Ads',
                    breadcrumb: 'Dashboard'
                }
            },
            {
                path: 'ads-rules',
                name: 'marketing-ads-rules',
                component: () => import('@/views/Marketing/MarketingAdsRules.vue'),
                meta: {
                    title: 'Regras Automáticas',
                    breadcrumb: 'Regras'
                }
            }
        ]
    },

    // Loja Escola — integração Shopify
    {
        path: '/loja-escola',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Loja Escola - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                redirect: '/loja-escola/produtos'
            },
            {
                path: 'produtos',
                name: 'loja-escola-produtos',
                component: () => import('@/views/LojaEscola/LojaEscolaProdutos.vue'),
                meta: { title: 'Produtos', breadcrumb: 'Produtos' }
            },
            {
                path: 'pedidos',
                name: 'loja-escola-pedidos',
                component: () => import('@/views/LojaEscola/LojaEscolaPedidos.vue'),
                meta: { title: 'Pedidos', breadcrumb: 'Pedidos' }
            },
            {
                path: 'pedidos/:id',
                name: 'loja-escola-pedido-detalhe',
                component: () => import('@/views/LojaEscola/LojaEscolaPedidoDetalhe.vue'),
                meta: { title: 'Detalhe do Pedido', breadcrumb: 'Pedido' }
            },
            {
                path: 'metricas',
                name: 'loja-escola-metricas',
                component: () => import('@/views/LojaEscola/LojaEscolaMetricas.vue'),
                meta: { title: 'Métricas', breadcrumb: 'Métricas' }
            },
            {
                path: 'metafields',
                name: 'loja-escola-metafields',
                component: () => import('@/views/LojaEscola/LojaEscolaMetafields.vue'),
                meta: { title: 'Metafields', breadcrumb: 'Metafields' }
            },
            {
                path: 'clientes',
                name: 'loja-escola-clientes',
                component: () => import('@/views/LojaEscola/LojaEscolaClientes.vue'),
                meta: { title: 'Clientes', breadcrumb: 'Clientes' }
            },
            {
                path: 'logs',
                name: 'loja-escola-logs',
                component: () => import('@/views/LojaEscola/LojaEscolaLogs.vue'),
                meta: { title: 'Logs de Sincronização', breadcrumb: 'Logs' }
            },
            {
                path: 'escolas',
                name: 'loja-escola-escolas',
                component: () => import('@/views/LojaEscola/LojaEscolaEscolas.vue'),
                meta: { title: 'Escolas', breadcrumb: 'Escolas' }
            },
            {
                path: 'vouchers',
                name: 'loja-escola-vouchers',
                component: () => import('@/views/LojaEscola/LojaEscolaVouchers.vue'),
                meta: { title: 'Vouchers', breadcrumb: 'Vouchers' }
            }
        ]
    },

    // E-commerce · Master Loja — proxy para a API externa Master Loja
    {
        path: '/ecommerce/master-loja',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Master Loja - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'master-loja-produtos',
                component: () => import('@/views/MasterLoja/MasterLojaProdutos.vue'),
                meta: { title: 'Master Loja', breadcrumb: 'Master Loja' }
            },
            {
                path: 'produtos/:id',
                name: 'master-loja-produto-detalhe',
                component: () => import('@/views/MasterLoja/MasterLojaProdutoDetalhe.vue'),
                meta: { title: 'Detalhe do Produto', breadcrumb: 'Produto' }
            },
            {
                path: 'categorias',
                name: 'master-loja-categorias',
                component: () => import('@/views/MasterLoja/MasterLojaCategorias.vue'),
                meta: { title: 'Categorias', breadcrumb: 'Categorias' }
            },
            {
                path: 'promocoes',
                name: 'master-loja-promocoes',
                component: () => import('@/views/MasterLoja/MasterLojaPromocoes.vue'),
                meta: { title: 'Promoções', breadcrumb: 'Promoções' }
            },
            {
                path: 'promocoes/nova',
                name: 'master-loja-promocao-nova',
                component: () => import('@/views/MasterLoja/MasterLojaPromocaoForm.vue'),
                meta: { title: 'Nova Promoção', breadcrumb: 'Nova promoção' }
            },
            {
                path: 'promocoes/:id',
                name: 'master-loja-promocao-detalhe',
                component: () => import('@/views/MasterLoja/MasterLojaPromocaoForm.vue'),
                meta: { title: 'Promoção', breadcrumb: 'Promoção' }
            },
            {
                path: 'campanhas',
                name: 'master-loja-campanhas',
                component: () => import('@/views/MasterLoja/MasterLojaCampanhas.vue'),
                meta: { title: 'Campanhas', breadcrumb: 'Campanhas' }
            },
            {
                path: 'campanhas/nova',
                name: 'master-loja-campanha-nova',
                component: () => import('@/views/MasterLoja/MasterLojaCampanhaForm.vue'),
                meta: { title: 'Nova Campanha', breadcrumb: 'Nova campanha' }
            },
            {
                path: 'campanhas/:id',
                name: 'master-loja-campanha-detalhe',
                component: () => import('@/views/MasterLoja/MasterLojaCampanhaForm.vue'),
                meta: { title: 'Campanha', breadcrumb: 'Campanha' }
            },
            // Banner do hero (SPEC-2026-900). 'novo' vem ANTES de ':id', senão o
            // Express/Vue casaria a palavra "novo" como id de banner.
            {
                path: 'banners',
                name: 'master-loja-banners',
                component: () => import('@/views/MasterLoja/MasterLojaBanners.vue'),
                meta: { title: 'Banners', breadcrumb: 'Banners' }
            },
            {
                path: 'banners/novo',
                name: 'master-loja-banner-novo',
                component: () => import('@/views/MasterLoja/MasterLojaBannerForm.vue'),
                meta: { title: 'Novo Banner', breadcrumb: 'Novo banner' }
            },
            {
                path: 'banners/:id',
                name: 'master-loja-banner-detalhe',
                component: () => import('@/views/MasterLoja/MasterLojaBannerForm.vue'),
                meta: { title: 'Banner', breadcrumb: 'Banner' }
            },
            // Operação da loja — leem a API do Master Loja pelo proxy do Hub
            // (o espelho Shopify local deixou de ser a fonte).
            {
                path: 'pedidos',
                name: 'master-loja-pedidos',
                component: () => import('@/views/MasterLoja/MasterLojaPedidos.vue'),
                meta: { title: 'Pedidos', breadcrumb: 'Pedidos' }
            },
            // Expedição (MSG-082 · SPEC-2026-1180). Rota de permissão PRÓPRIA, e não
            // um sub-item do módulo: quem embala caixa não recebe promoção e banner
            // junto.
            //
            // Rota ÚNICA, sem `/:numero`: o detalhe é drawer sobre a fila (§3.4 do
            // contrato de migração), como nas outras listas do módulo. Quem embala
            // trabalha sobre a lista, um pedido atrás do outro.
            {
                path: 'expedicao',
                name: 'master-loja-expedicao',
                component: () => import('@/views/MasterLoja/MasterLojaExpedicao.vue'),
                meta: { title: 'Expedição', breadcrumb: 'Expedição' }
            },
            {
                path: 'clientes',
                name: 'master-loja-clientes',
                component: () => import('@/views/MasterLoja/MasterLojaClientes.vue'),
                meta: { title: 'Clientes', breadcrumb: 'Clientes' }
            },
            {
                path: 'integracao-catalogo',
                name: 'master-loja-integracao-catalogo',
                component: () => import('@/views/MasterLoja/MasterLojaIntegracaoCatalogo.vue'),
                meta: { title: 'Integração de catálogo', breadcrumb: 'Integração de catálogo' }
            },
            {
                path: 'integracao-protheus',
                name: 'master-loja-integracao-protheus',
                component: () => import('@/views/MasterLoja/MasterLojaIntegracaoProtheus.vue'),
                meta: { title: 'Integração Protheus', breadcrumb: 'Integração Protheus' }
            },
            {
                path: 'auditoria-protheus',
                name: 'master-loja-auditoria-protheus',
                component: () => import('@/views/MasterLoja/MasterLojaAuditoriaProtheus.vue'),
                meta: { title: 'Logs e auditoria', breadcrumb: 'Logs e auditoria' }
            }
        ]
    },

    // 3. Bloco independente do Financeiro
    {
        path: '/gateway',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Financeiro - Hub AtlanGlobal'
        },
        children: [
            {
                path: 'lista-pagamentos',
                name: 'payment-list',
                component: () => import('@/views/Payments/PaymentList.vue'),
                meta: {
                    title: 'Histórico de Pagamentos',
                    breadcrumb: 'Pagamentos'
                }
            },
            {
                path: 'novo-pagamento', // Vai virar /financeiro/novo-pagamento
                name: 'direct-payment',
                component: () => import('@/views/Payments/DirectCheckout.vue'),
                meta: {
                    title: 'Novo Link de Pagamento',
                    breadcrumb: 'Novo Pagamento'
                }
            },
            {
                path: 'clientes',
                name: 'customers',
                component: () => import('@/views/Management/AsaasCustomerManagement.vue'),
                meta: {
                    title: 'Visão de Clientes Asaas',
                    breadcrumb: 'Clientes'
                }
            },
        ]
    },
    {
        path: '/asaas',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Gestão - Asaas'
        },
        children: [
            {
                path: 'webhooks',
                name: 'webhooks',
                component: () => import('@/views/Management/AsaasWebhookManagement.vue'),
                meta: {
                    title: 'Visão de Webhooks',
                    breadcrumb: 'Webhooks'
                }
            }
        ]
    },
    {
        path: '/profile',
        meta: {
            requiresAuth: true,
        },
        component: () => import('@/views/Users/ProfileRedirect.vue'),
    },
    {
        path: '/plataformas',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            breadcrumb: 'Plataformas'
        },
        children: [
            {
                path: 'simrobotica',
                name: 'PlataformaSimRobotica', // Nome único para a permissão
                component: () => import('@/views/Atlan/School/SchoolManager.vue'),
                props: { product: 'simrobotica' }, // Passamos a prop manualmente aqui!
                meta: {
                    title: 'Gerenciador SIMRobótica',
                    breadcrumb: 'SIMRobótica'
                }
            },
            {
                path: 'simroboticaplus',
                name: 'PlataformaSimRoboticaPlus',
                component: () => import('@/views/Atlan/School/SchoolManager.vue'),
                props: { product: 'simroboticaplus' },
                meta: {
                    title: 'Gerenciador SIMRobótica Plus',
                    breadcrumb: 'SIMRobótica Plus'
                }
            },
            {
                path: 'astromaker',
                name: 'PlataformaAstromaker',
                component: () => import('@/views/Atlan/School/SchoolManager.vue'),
                props: { product: 'astromaker' },
                meta: {
                    title: 'Gerenciador Astromaker',
                    breadcrumb: 'Astromaker'
                }
            },
            {
                path: 'prodigius',
                name: 'PlataformaProdigius',
                component: () => import('@/views/Atlan/School/SchoolManager.vue'),
                props: { product: 'prodigius' },
                meta: {
                    title: 'Gerenciador Prodigius',
                    breadcrumb: 'Prodigius'
                }
            },
            {
                path: 'adventotech',
                name: 'PlataformaAdventoTech',
                component: () => import('@/views/Atlan/School/SchoolManager.vue'),
                props: { product: 'adventotech' },
                meta: {
                    title: 'Gerenciador Advento Tech',
                    breadcrumb: 'Advento Tech'
                }
            }
        ]
    },
    {
        path: '/users',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Users - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'Users',
                component: () => import('@/views/Users/Users.vue'),
                meta: {
                    title: 'Users'
                }
            },
            {
                path: ':samaccountname',
                name: 'UserProfile',
                component: () => import('@/views/Users/UserProfile.vue'),
                meta: {
                    title: 'Perfil do Usuário'
                }
            }
        ]
    },
    // --- APIs → Notificação de eventos (n8n / Teams) — subitem do menu APIs (Admin Master) ---
    {
        path: '/apis/notificacoes',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            requiresAdminMaster: true,
            title: 'Notificação de eventos - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'ApisNotificacoes',
                component: () => import('@/views/Admin/ApisIntegracoes.vue'),
                meta: {
                    requiresAdminMaster: true,
                    title: 'Notificação de eventos',
                    breadcrumb: 'Notificação de eventos'
                }
            }
        ]
    },
    // --- APIs → E-mail (SendGrid) — self-service do tenant (SEM requiresAdminMaster) ---
    {
        path: '/apis/email',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'E-mail (SendGrid) - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'ApisEmail',
                component: () => import('@/views/Admin/ApisEmail.vue'),
                meta: {
                    title: 'E-mail (SendGrid)',
                    breadcrumb: 'E-mail (SendGrid)'
                }
            }
        ]
    },
    // --- APIs → Armazenamento (R2) — self-service do tenant (SEM requiresAdminMaster) ---
    {
        path: '/apis/armazenamento',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Armazenamento (R2) - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'ApisArmazenamento',
                component: () => import('@/views/Admin/ApisArmazenamento.vue'),
                meta: {
                    title: 'Armazenamento (R2)',
                    breadcrumb: 'Armazenamento (R2)'
                }
            }
        ]
    },
    // --- APIs → Gestão API — painel unificado de integrações (self-service do tenant) ---
    {
        path: '/apis/gestao',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Gestão API - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'ApisGestao',
                component: () => import('@/views/Admin/GestaoApi/GestaoApiPage.vue'),
                meta: {
                    title: 'Gestão API',
                    breadcrumb: 'Gestão API'
                }
            }
        ]
    },
    // --- APIs → Destinos do switcher — sistemas/ambientes do seletor da sidebar ---
    {
        path: '/apis/destinos',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Destinos do switcher - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'ApisDestinos',
                component: () => import('@/views/Admin/SwitcherDestinos/SwitcherDestinosPage.vue'),
                meta: {
                    title: 'Destinos do switcher',
                    breadcrumb: 'Destinos do switcher'
                }
            }
        ]
    },
    // --- Identidade (Protheus ↔ Hub): a tela mudou para /reembolso/identidade (spec 2026-10-15) ---
    // O caminho velho segue vivo como REDIRECT: pendências do ERP gravadas em `protheus_erro`
    // citam "(/apis/identidade)", e favoritos/links antigos também. Query e hash vão junto.
    {
        path: '/apis/identidade',
        redirect: identidadeRedirect
    },
    // --- PERMISSÕES (Rotas, Usuários, Grupos) ---
    {
        path: '/admin/tenants',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            requiresAdminMaster: true,
            masterOnly: true,
            title: 'Administração de Tenants - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'TenantsAdmin',
                component: () => import('@/views/Admin/Tenants/TenantsAdmin.vue'),
                meta: {
                    requiresAdminMaster: true,
                    masterOnly: true,
                    title: 'Tenants',
                    breadcrumb: 'Administração de Tenants'
                }
            }
        ]
    },
    {
        path: '/permissions',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            requiresAdminMaster: true,
            title: 'Permissões - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'RoutePermissions',
                component: () => import('@/views/Permissions/RoutePermissions.vue'),
                meta: {
                    requiresAdminMaster: true,
                    title: 'Rotas',
                    breadcrumb: 'Permissões'
                }
            },
            {
                path: 'matrix',
                name: 'PermissionMatrix',
                component: () => import('@/views/Permissions/PermissionMatrix.vue'),
                meta: {
                    requiresAdminMaster: true,
                    title: 'Matriz de permissões',
                    breadcrumb: 'Matriz'
                }
            },
            {
                path: 'users',
                name: 'PermissionsUsers',
                component: () => import('@/views/Permissions/Users.vue'),
                meta: {
                    requiresAdminMaster: true,
                    title: 'Usuários',
                    breadcrumb: 'Usuários'
                }
            },
            {
                path: 'users/:samaccountname',
                name: 'UserPermissionsEdit',
                component: () => import('@/views/Permissions/UserPermissionsEdit.vue'),
                props: true,
                meta: {
                    requiresAdminMaster: true,
                    title: 'Editar Permissões',
                    breadcrumb: 'Editar'
                }
            },
            {
                path: 'custom-groups',
                name: 'CustomGroups',
                component: () => import('@/views/Permissions/CustomGroups.vue'),
                meta: {
                    requiresAdminMaster: true,
                    title: 'Grupos Personalizados',
                    breadcrumb: 'Grupos'
                }
            }
        ]
    },
    {
        path: '/courses',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Cursos - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'courses',
                component: () => import('@/views/Courses/Courses.vue'),
                meta: {
                    title: 'Cursos',
                    requiresAuth: true,
                    breadcrumb: 'Cursos'
                }
            },
            {
                path: 'admin',
                redirect: '/courses/admin/courses'
            },
            {
                path: 'admin/courses',
                name: 'admin-courses',
                component: () => import('@/views/Courses/Admin/CoursesAdmin.vue'),
                meta: { title: 'Admin Cursos', breadcrumb: 'Cursos' }
            },
            {
                path: 'admin/courses/new',
                name: 'admin-course-create',
                component: () => import('@/views/Courses/Admin/CourseCreate.vue'),
                meta: { title: 'Novo Curso', breadcrumb: 'Novo' }
            },
            {
                path: 'admin/courses/:id',
                name: 'admin-course-detail',
                component: () => import('@/views/Courses/Admin/CourseDetail.vue'),
                props: true,
                meta: { title: 'Detalhes do Curso', breadcrumb: 'Curso' }
            },
            {
                path: 'admin/courses/:id/edit',
                name: 'admin-course-edit',
                component: () => import('@/views/Courses/Admin/CourseEdit.vue'),
                props: true,
                meta: { title: 'Editar Curso', breadcrumb: 'Editar' }
            },
            {
                path: 'admin/modules',
                name: 'admin-modules',
                component: () => import('@/views/Courses/Admin/ModulesAdmin.vue'),
                meta: { title: 'Módulos', breadcrumb: 'Módulos' }
            },
            {
                path: 'admin/modules/new',
                name: 'admin-module-create',
                component: () => import('@/views/Courses/Admin/ModuleCreate.vue'),
                meta: { title: 'Novo Módulo', breadcrumb: 'Novo' }
            },
            {
                path: 'admin/modules/:id',
                name: 'admin-module-detail',
                component: () => import('@/views/Courses/Admin/ModuleDetail.vue'),
                props: true,
                meta: { title: 'Detalhes do Módulo', breadcrumb: 'Módulo' }
            },
            {
                path: 'admin/modules/:id/edit',
                name: 'admin-module-edit',
                component: () => import('@/views/Courses/Admin/ModuleEdit.vue'),
                props: true,
                meta: { title: 'Editar Módulo', breadcrumb: 'Editar' }
            },
            {
                path: 'admin/classes',
                name: 'admin-classes',
                component: () => import('@/views/Courses/Admin/ClassesAdmin.vue'),
                meta: { title: 'Aulas', breadcrumb: 'Aulas' }
            },
            {
                path: 'admin/classes/new',
                name: 'admin-class-create',
                component: () => import('@/views/Courses/Admin/ClassCreate.vue'),
                meta: { title: 'Nova Aula', breadcrumb: 'Nova' }
            },
            {
                path: 'admin/classes/:id',
                name: 'admin-class-detail',
                component: () => import('@/views/Courses/Admin/ClassDetail.vue'),
                props: true,
                meta: { title: 'Detalhes da Aula', breadcrumb: 'Aula' }
            },
            {
                path: 'admin/classes/:id/edit',
                name: 'admin-class-edit',
                component: () => import('@/views/Courses/Admin/ClassEdit.vue'),
                props: true,
                meta: { title: 'Editar Aula', breadcrumb: 'Editar' }
            },
            {
                path: 'admin/classes/:id/evaluation',
                name: 'admin-class-evaluation',
                component: () => import('@/views/Courses/Admin/EvaluationCreate.vue'),
                props: true,
                meta: { title: 'Avaliação', breadcrumb: 'Avaliação' }
            },
            {
                path: ':productId/class/:classId',
                name: 'class-viewer',
                component: () => import('@/views/Courses/ClassViewer.vue'),
                props: true,
                meta: {
                    requiresAuth: true,
                    title: 'Assistir Aula - Hub AtlanGlobal'
                }
            },
            {
                path: ':id(\\d+)',
                name: 'course-detail',
                component: () => import('@/views/Courses/CourseDetail.vue'),
                props: true
            }
        ]
    },
    {
        path: '/bookings',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Reuniões - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'BookingsDashboard',
                component: () => import('@/views/Bookings/Dashboard.vue'),
                meta: { title: 'Dashboard', breadcrumb: 'Reuniões' }
            },
            {
                path: 'new',
                name: 'NewBooking',
                component: () => import('@/views/Bookings/BookingWizard.vue'),
                meta: { title: 'Agendar Reunião', breadcrumb: 'Nova' }
            },
            {
                path: 'my-bookings',
                name: 'MyBookings',
                component: () => import('@/views/Bookings/MyBookings.vue'),
                meta: { title: 'Minhas Reuniões', breadcrumb: 'Minhas Reuniões' }
            },
            {
                path: 'all-bookings',
                name: 'AllBookings',
                component: () => import('@/views/Bookings/AllBookings.vue'),
                meta: { title: 'Todas as Reservas', breadcrumb: 'Reservas' }
            },
            {
                path: 'edit/:id',
                name: 'EditBooking',
                component: () => import('@/views/Bookings/BookingWizard.vue'),
                props: true,
                meta: { title: 'Editar Reserva', breadcrumb: 'Editar' }
            },
            // Admin: salas (antes de :id para não serem capturadas como id)
            {
                path: 'admin/rooms',
                name: 'BookingsAdminRooms',
                component: () => import('@/views/Bookings/admin/AllRooms.vue'),
                meta: { title: 'Salas', breadcrumb: 'Salas' }
            },
            {
                path: 'admin/rooms/new',
                name: 'BookingsAdminRoomCreate',
                component: () => import('@/views/Bookings/admin/RoomCreate.vue'),
                meta: { title: 'Nova Sala', breadcrumb: 'Nova Sala' }
            },
            {
                path: 'admin/rooms/:id',
                name: 'BookingsAdminRoomDetail',
                component: () => import('@/views/Bookings/admin/RoomDetail.vue'),
                props: true,
                meta: { title: 'Detalhes da Sala', breadcrumb: 'Sala' }
            },
            {
                path: 'admin/rooms/:id/edit',
                name: 'BookingsAdminRoomEdit',
                component: () => import('@/views/Bookings/admin/RoomEdit.vue'),
                props: true,
                meta: { title: 'Editar Sala', breadcrumb: 'Editar' }
            },
            // Ver reserva por id (por último para não capturar new, edit, admin, my-bookings, all-bookings)
            {
                path: ':id(\\d+)',
                name: 'BookingDetail',
                component: () => import('@/views/Bookings/BookingWizard.vue'),
                props: true,
                meta: { title: 'Detalhes da Reserva', breadcrumb: 'Reserva' }
            }
        ]
    },
    {
        path: '/indoor-tv',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            title: 'Mídia indoor TV - Hub AtlanGlobal'
        },
        children: [
            {
                path: 'admin',
                name: 'IndoorTvAdmin',
                component: () => import('@/views/IndoorTv/IndoorTvAdmin.vue'),
                meta: { title: 'Mídia indoor TV', breadcrumb: 'Mídia TV' }
            }
        ]
    },
    {
        path: '/logs',
        component: FullLayout,
        meta: {
            requiresAuth: true,
            requiresAdminMaster: true,
            title: 'Logs de Usuários - Hub AtlanGlobal'
        },
        children: [
            {
                path: '',
                name: 'UserLogs',
                component: () => import('@/views/Logs/UserLogs.vue'),
                meta: { requiresAdminMaster: true }
            }
        ]
    },

    // ITAM - Gestão de Ativos (/itam/*)
    ...assetsRoutes,
    // Políticas corporativas (/policies/*)
    ...policiesRoutes,
    // PCO / Faturas (/resources/*)
    ...resourcesRoutes,
    // Catálogo de Produtos Comerciais
    ...produtosRoutes,
    // Logística — Saldo Estoque (Protheus mirror)
    ...logisticaRoutes,
    // Módulo de Reembolso
    ...reembolsoRoutes,
    // Módulo de Inovação
    ...inovacaoRoutes,
    // Pedidos de Compra (Protheus PC)
    ...pedidosCompraRoutes,
    ...licitacoesRoutes,
    ...faturasTiRoutes,
    ...auditoriaComodatoRoutes,
    // Controle de Acesso (faciais): Cadastro · Agenda · Device · Log
    ...controleAcessoRoutes,
    // Wiki corporativa
    ...wikiRoutes,
    // Cartão Corporativo (Financeiro)
    ...cartaoCorporativoRoutes,
    ...financeiroRelatoriosRoutes,
    // Compras > Relatórios > Relatório de Compras (rateio de pedidos, Protheus live)
    ...comprasRelatoriosRoutes,
    ...ticketRoutes,
    // Relatórios do Service Desk (shell console; path /ticket/relatorios preservado)
    ...ticketReportsRoutes,
    // Compass · planejamento semanal dos diretores (shell console; 2 rotas restritas)
    ...compassRoutes,
    // Controladoria — Ativos em Poder de Terceiros
    ...ativosRoutes,
    // Central de Gestão de Usuários M365 (só no design system novo)
    ...m365Routes,
    // Relatório Municipal Consolidado (fontes públicas: IBGE/TSE/INEP/Câmara)
    ...relatorioMunicipalRoutes,
    // Dashboards › P&D tecnológico (placar mensal de TI; rota restrita)
    ...dashboardsRoutes
];

export const specialRoutes: RouteRecordRaw[] = [
    {
        path: '/:pathMatch(.*)*',
        name: 'Error404',
        component: () => import('@/views/Error404.vue'),
        meta: {
            requiresAuth: true,
            title: 'Página não encontrada'
        }
    }
];

export const requiresAuthentication = (route: any): boolean => {
    return route.meta?.requiresAuth === true;
};

export const hideForAuthenticated = (route: any): boolean => {
    return route.meta?.hideForAuth === true;
};
